import React, { useState, useEffect, useContext } from 'react';
import { X, Loader2, AlertCircle, ArrowRightLeft } from 'lucide-react';
import { UsuarioContext } from '../../../providers/personaContext';
import { getTecnicosDisponibles, traspasarCita } from '../api';

/**
 * ModalTraspaso — Fase 25 · #25
 *
 * El técnico traspasa una cita suya a otro técnico de la misma campaña.
 * Autocontenido (patrón MarcarCitaModal). Reversible → sin doble seguro.
 *
 * Backend: POST /citas/:id/traspasar { tecnico_nuevo_id, notas? }
 *   409 conflicto_horario → se muestra inline y el modal queda abierto.
 */
export default function ModalTraspaso({ cita, onClose, onOk }) {
  const { persona } = useContext(UsuarioContext);
  const miId = persona?.id;

  const [tecnicos, setTecnicos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [seleccion, setSeleccion] = useState(null);
  const [notas, setNotas] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let vivo = true;
    getTecnicosDisponibles(cita.campana_id)
      .then((res) => {
        if (!vivo) return;
        // Excluir al propio técnico (el que traspasa). Number() blinda contra
        // tipos mixtos (endpoint devuelve number; persona.id viene del JWT).
        setTecnicos((res.data || []).filter((t) => Number(t.id) !== Number(miId)));
      })
      .catch(() => { if (vivo) setError('No se pudieron cargar los técnicos.'); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [cita.campana_id, miId]);

  async function confirmar() {
    if (!seleccion) return;
    setGuardando(true);
    setError(null);
    try {
      await traspasarCita(cita.id, { tecnico_nuevo_id: seleccion, notas: notas.trim() || undefined });
      if (onOk) await onOk();
    } catch (e) {
      const data = e?.response?.data;
      // 409 conflicto_horario y demás: mensaje inline, modal abierto.
      setError(data?.error || 'No se pudo traspasar la cita.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center px-4 py-8 overflow-y-auto"
      onClick={guardando ? undefined : onClose}
    >
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4 text-slate-500" />
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Traspasar cita</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {cita.fecha} · {cita.hora_inicio} · {cita.usuario?.nombre_completo}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={guardando}
            className="w-7 h-7 grid place-items-center rounded-md hover:bg-slate-100 disabled:opacity-50"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Cuerpo */}
        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          <div>
            <p className="text-xs font-medium text-slate-700 mb-2">Traspasar a</p>
            {cargando ? (
              <div className="py-6 text-center text-sm text-slate-500">
                <Loader2 className="w-4 h-4 inline-block animate-spin mr-2" />
                Cargando técnicos…
              </div>
            ) : tecnicos.length === 0 ? (
              <p className="text-sm text-slate-500 bg-slate-50 border border-slate-100 rounded-md p-3">
                No hay otros técnicos en esta campaña.
              </p>
            ) : (
              <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                {tecnicos.map((t) => (
                  <li key={t.id}>
                    <label className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-slate-50 cursor-pointer">
                      <input
                        type="radio"
                        name="tecnico-destino"
                        checked={seleccion === t.id}
                        onChange={() => setSeleccion(t.id)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-slate-800 flex-1">{t.nombre_completo}</span>
                      <span className="text-xs text-slate-400">
                        {t.carga_activa} {t.carga_activa === 1 ? 'cita activa' : 'citas activas'}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="text-xs font-medium text-slate-700 mb-2">
              Motivo <span className="text-slate-400 font-normal">(opcional)</span>
            </p>
            <textarea
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder='Ej. "Tengo la agenda llena esa semana"'
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 resize-none"
            />
          </div>

          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2.5 flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-100 bg-slate-50 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            disabled={guardando}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={confirmar}
            disabled={guardando || !seleccion}
            className="px-3 py-1.5 text-sm rounded-lg text-white flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-60"
          >
            {guardando && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Traspasar
          </button>
        </div>
      </div>
    </div>
  );
}
